import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

export interface EnrolmentHeadingProps {
  /** The small caps line above the heading — `DEVICE SETUP`, or `CONNECTING` on frame 06. */
  readonly eyebrow: string;
  readonly heading: string;
  /** The sentence under the heading. Absent on no drawn frame, and optional only for the undrawn states. */
  readonly body?: string;
}

/**
 * The `Heading` block every state in this section opens with [Figma · `Heading`, all 18 frames].
 *
 * Values read from the file: the eyebrow is 12/16 at `letterSpacing: 1.44` in `--muted-foreground`, the
 * heading 24/28 bold at `-0.36` in `--foreground`, the body 14/20 in `--muted-foreground`, the three stacked
 * 8px apart and centred. Identical at 1440, 820 and 375 — only the column width changes.
 *
 * The `<h1>` takes focus on mount so a screen reader is told the screen changed: the kiosk swaps whole
 * screens with no page load, and the error countdown and the lockout are states a staffer has to be TOLD
 * about, not shown [AC-10].
 */
export function EnrolmentHeading({ eyebrow, heading, body }: EnrolmentHeadingProps) {
  const focus = useFocusOnMount<HTMLHeadingElement>();

  return (
    <div className="flex w-full flex-col items-center gap-2">
      <p className="w-full text-center text-xs uppercase leading-4 tracking-[1.44px] text-[var(--muted-foreground)]">
        {eyebrow}
      </p>
      <h1
        className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
        ref={focus}
        tabIndex={-1}
      >
        {heading}
      </h1>
      {body !== undefined && (
        <p className="w-full text-center text-sm leading-5 text-[var(--muted-foreground)]">
          {body}
        </p>
      )}
    </div>
  );
}
